/**
 * Local store for AI-generated suggestions (cohorts, message variants, etc.).
 *
 * Persists in localStorage so suggestions survive page reloads. Each "type"
 * has its own bucket. Suggestions carry an internal `_id` so the UI can
 * track which ones the operator has accepted / dismissed.
 *
 * Local-only.
 */

const KEY_PREFIX = "xnotify-ai-suggestions";

function key(type, customerId) {
  return `${KEY_PREFIX}.${type}.cid${customerId || 0}`;
}

export function getSuggestions(type, customerId) {
  try {
    const raw = localStorage.getItem(key(type, customerId));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

export function setSuggestions(type, customerId, list) {
  try {
    localStorage.setItem(key(type, customerId), JSON.stringify(list || []));
    window.dispatchEvent(new CustomEvent("xnotify-ai-suggestions-changed", { detail: { type, customerId } }));
  } catch { /* ignore quota */ }
}

/**
 * Append new suggestions (de-dup by name). Returns the merged list.
 *
 * DA-FEAT — accepts an optional `runMeta = { run_id }` so each new
 * suggestion is tagged with the audit-log run that produced it. When
 * the operator later accepts or rejects, the feedback POST carries
 * the run_id + the agent's own item id so RRS can update the right
 * row in tbl_agent_run_items.
 */
export function appendSuggestions(type, customerId, incoming, runMeta = {}) {
  const existing = getSuggestions(type, customerId);
  const byName = new Map(existing.map((s) => [s.name, s]));
  const now = Date.now();
  const runId = runMeta?.run_id || null;
  for (const s of incoming || []) {
    if (!s || !s.name) continue;
    const id = `ai-${type}-${now}-${Math.random().toString(36).slice(2, 8)}`;
    if (!byName.has(s.name)) {
      byName.set(s.name, {
        _id: id,
        _saved_at: new Date().toISOString(),
        _status: "pending",
        // DA-FEAT — feedback correlation. _run_id ties back to
        // tbl_agent_runs.run_id; _item_id is the agent's own
        // cohort id (preserves through dedup).
        _run_id: runId,
        _item_id: s.id || s.cohort_id || s._id || null,
        ...s
      });
    }
  }
  const merged = [...byName.values()].sort((a, b) => (b._saved_at || "").localeCompare(a._saved_at || ""));
  setSuggestions(type, customerId, merged);
  return merged;
}

/** Mark one suggestion accepted. */
export function markAccepted(type, customerId, suggestionId, extra = {}) {
  const list = getSuggestions(type, customerId).map((s) =>
    s._id === suggestionId ? { ...s, _status: "accepted", _accepted_at: new Date().toISOString(), ...extra } : s
  );
  setSuggestions(type, customerId, list);
  // DA-FEAT — write the feedback to RRS so the agent-runs table
  // reflects this acceptance. Fire-and-forget; localStorage stays
  // authoritative for UI state.
  const accepted = list.find((s) => s._id === suggestionId);
  if (accepted && accepted._run_id) {
    postFeedback(accepted._run_id, accepted._item_id || suggestionId, "accept", {
      saved_segment_id: extra.savedId || extra.cohort_id || null,
    }).catch(() => {});
  }
}

/** Mark one suggestion rejected (NEW — for explicit operator dismiss). */
export function markRejected(type, customerId, suggestionId, reason = null) {
  const list = getSuggestions(type, customerId).map((s) =>
    s._id === suggestionId ? { ...s, _status: "rejected", _rejected_at: new Date().toISOString(), _rejection_reason: reason } : s
  );
  setSuggestions(type, customerId, list);
  const rejected = list.find((s) => s._id === suggestionId);
  if (rejected && rejected._run_id) {
    postFeedback(rejected._run_id, rejected._item_id || suggestionId, "reject", {
      rejection_reason: reason || null,
    }).catch(() => {});
  }
}

/**
 * DA-FEAT — POST acceptance/rejection to RRS. Used by the self-
 * learning feedback loop. Failure is non-fatal — UI state stays in
 * localStorage so the operator sees their action immediately.
 */
async function postFeedback(run_id, item_id, action, body = {}) {
  if (!run_id || !item_id || !action) return null;
  const apiKey = localStorage.getItem("api-key");
  const accessToken = localStorage.getItem("access-token") || localStorage.getItem("x-access-token");
  const apiBase = process.env.REACT_APP_API_URL || "";
  const url = `${apiBase.replace(/\/$/, "")}/agent-runs/${encodeURIComponent(run_id)}/items/${encodeURIComponent(item_id)}/action`;
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(apiKey ? { "x-api-key": apiKey } : {}),
        ...(accessToken ? { "x-access-token": accessToken } : {}),
      },
      body: JSON.stringify({ action, ...body }),
    });
    if (!res.ok) {
      // Log silently — feedback persistence shouldn't surface errors to operator
      // eslint-disable-next-line no-console
      console.warn(`[ai-feedback] ${action} for ${run_id}/${item_id} returned ${res.status}`);
      return null;
    }
    return await res.json().catch(() => null);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[ai-feedback] ${action} failed: ${e.message}`);
    return null;
  }
}

/**
 * Cross-check the list of suggestions against the tenant's real saved cohorts
 * (or generic items, depending on type). If a saved cohort matches a
 * suggestion (by name, or by canonical-condition fingerprint), mark that
 * suggestion accepted. Useful after the operator returns from /cohorts/create.
 */
export function reconcileWithSaved(type, customerId, savedItems = []) {
  if (!Array.isArray(savedItems) || savedItems.length === 0) return;
  const savedNames = new Set(savedItems.map((s) => (s.name || "").trim().toLowerCase()).filter(Boolean));
  const list = getSuggestions(type, customerId);
  let changed = false;
  const next = list.map((s) => {
    if (s._status === "accepted") return s;
    const key = (s.name || "").trim().toLowerCase();
    if (key && savedNames.has(key)) {
      changed = true;
      return { ...s, _status: "accepted", _accepted_at: new Date().toISOString(), _reconciled: true };
    }
    return s;
  });
  if (changed) setSuggestions(type, customerId, next);
}

/** Dismiss / delete a suggestion. Treats dismissal as an explicit reject for the feedback loop. */
export function dismissSuggestion(type, customerId, suggestionId, reason = null) {
  // Mark rejected (POSTs feedback) BEFORE removing from the list,
  // because postFeedback needs the suggestion's _run_id + _item_id.
  const target = getSuggestions(type, customerId).find((s) => s._id === suggestionId);
  if (target && target._run_id) {
    postFeedback(target._run_id, target._item_id || suggestionId, "reject", { rejection_reason: reason }).catch(() => {});
  }
  const list = getSuggestions(type, customerId).filter((s) => s._id !== suggestionId);
  setSuggestions(type, customerId, list);
}

/** Clear all of a given type. */
export function clearSuggestions(type, customerId) {
  setSuggestions(type, customerId, []);
}

/** Subscribe to changes (used by the Accordion in the Cohorts page). */
export function onChange(handler) {
  const wrapped = (ev) => handler(ev.detail || {});
  window.addEventListener("xnotify-ai-suggestions-changed", wrapped);
  return () => window.removeEventListener("xnotify-ai-suggestions-changed", wrapped);
}
